import React, { useEffect, useMemo, useState } from 'react';
import DefaultMediaFallback from '../../components/common/DefaultMediaFallback';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { getPublicErrorMessage } from '../../utils/errors';

/**
 * Renders the movie add search experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.results - results value.
 * @param {*} props.onCancel - Callback invoked when cancel occurs.
 * @param {*} props.onManualEntry - Callback invoked when manual entry occurs.
 * @param {*} props.onSearch - Callback invoked when search occurs.
 * @param {*} props.onUseMovie - Callback invoked when use movie occurs.
 * @param {*} props.isSearching - Whether is searching is enabled.
 * @param {*} props.error - Failure being normalized or reported.
 * @returns {JSX.Element} The rendered interface.
 */
export default function MovieAddSearch({ results, onCancel, onManualEntry, onSearch, onUseMovie, isSearching, error }) {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= 2;

  useEffect(() => {
    if (!canSearch) return undefined;

    const timer = window.setTimeout(() => {
      onSearch(trimmedQuery);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [canSearch, onSearch, trimmedQuery]);

  const visibleResults = useMemo(() => (canSearch && !isSearching ? results : []), [canSearch, isSearching, results]);

  return (
    <div className="movie-add-search">
      <button type="button" className="recipe-back-link" onClick={onCancel}>← Back</button>

      <section className="recipe-form-section priority-section">
        <div className="form-group">
          <label>{mediaStrings.movies.addSearch.label}</label>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={mediaStrings.movies.addSearch.placeholder}
            autoFocus
          />
        </div>

        {isSearching && <p className="muted-copy movie-search-status">{moduleCommonStrings.actions.searching}</p>}
        {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

        <div className="movie-import-results movie-add-results">
          {visibleResults.map((movie) => (
            <button type="button" className="movie-import-result" key={movie.tmdbId} onClick={() => onUseMovie(movie)}>
              {movie.posterUrl ? (
                <img src={movie.posterUrl} alt="" />
              ) : (
                <DefaultMediaFallback className="movie-poster-placeholder" fallbackType="movies" />
              )}
              <div>
                <h3>{movie.title}</h3>
                <p>
                  {[movie.releaseYear, movie.director && mediaStrings.movies.addSearch.directedBy(movie.director)]
                    .filter(Boolean)
                    .join(' - ')}
                </p>
                <p>{movie.overview}</p>
                <div className="recipe-tags">
                  {movie.genres.map((genre) => <span key={genre}>{genre}</span>)}
                </div>
              </div>
            </button>
          ))}
        </div>

        {canSearch && !isSearching && visibleResults.length === 0 && (
          <div className="empty-state movie-import-empty">
            <p>{mediaStrings.movies.addSearch.empty}</p>
            <button type="button" className="btn-secondary" onClick={onManualEntry}>
              {moduleCommonStrings.actions.addManually}
            </button>
          </div>
        )}

        <p className="muted-copy movie-search-status">{mediaStrings.movies.addSearch.attribution}</p>
      </section>

    </div>
  );
}
